import { Handle, Position, type NodeProps } from '@xyflow/react';
import { T, useGT } from 'gt-react';
import { cn } from 'src/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  EXPRESSION_INPUT_HANDLE_ID,
  EXPRESSION_OUTPUT_HANDLE_ID,
  type ExpressionNodeData,
} from 'src/lib/workflows/expressionOverlay';
import { useSocketExpressionBuilderActions } from './expression-builder/builderContext';

/**
 * The socket-expression badge: one per expression-fed input socket, sitting
 * just before the socket where the expression's dashed wires converge. It
 * shows the ROOT call's glyph — `join` for `(string_join …)`, `[ ]` for a
 * collect — and nothing else; nested calls never get a badge of their own.
 *
 * ```
 *   o- - -\
 *          - -[join]--o
 *   o- - -/
 * ```
 *
 * Clicking it opens the socket expression builder inline, in the badge's
 * place (when the canvas has one — the read-only graph view does not). Hover
 * carries what the badge cannot: the full call name, what it does, where each
 * argument comes from, and the whole expression with step names in place of
 * ids.
 *
 * Purely derived: the overlay builder appends these at render time (see
 * `expressionOverlay.ts`), so nothing here mutates the graph.
 */

/**
 * Invisible, 1px, unclickable. React Flow still needs a real handle to anchor
 * an edge to, so it can't be dropped — only hidden.
 */
const HIDDEN_HANDLE =
  '!h-px !w-px !min-h-px !min-w-px !rounded-none !border-0 !bg-transparent';

/** Every handle sits at the badge's vertical center — input and output alike. */
const CENTERED_HANDLE = '!top-1/2 !-translate-y-1/2 !transform-none';

const BADGE_STATE_CLASS: Record<ExpressionNodeData['state'], string> = {
  idle: 'border border-gray-400 bg-gray-100 text-gray-800 dark:border-gray-500 dark:bg-gray-800 dark:text-gray-200',
  editing:
    'border border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-200',
  dimmed:
    'border border-gray-300 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-500',
};

export function ExpressionNode({ data }: NodeProps) {
  const gt = useGT();
  const nodeData = data as unknown as ExpressionNodeData;
  const builder = useSocketExpressionBuilderActions();

  const body = (
    <>
      {/* The function, and nothing else — never truncated. Its arguments,
          literals included, are hover's job. */}
      <span>{nodeData.label}</span>
      <Handle
        type="target"
        position={Position.Left}
        id={EXPRESSION_INPUT_HANDLE_ID}
        isConnectable={false}
        className={cn(HIDDEN_HANDLE, CENTERED_HANDLE, '!-left-px')}
      />
      <Handle
        type="source"
        position={Position.Right}
        id={EXPRESSION_OUTPUT_HANDLE_ID}
        isConnectable={false}
        className={cn(HIDDEN_HANDLE, CENTERED_HANDLE, '!-right-px')}
      />
    </>
  );

  const className = cn(
    'relative flex h-full w-full items-center justify-center rounded-[3px] px-[5px]',
    'font-mono text-[10px] leading-none font-medium whitespace-nowrap',
    BADGE_STATE_CLASS[nodeData.state],
    // React Flow gives a node `pointer-events: none` unless it is selectable,
    // draggable, or the canvas passes node mouse handlers — and the badge is
    // deliberately none of those, so without this it never receives the hover
    // that opens its tooltip. `nodrag`/`nopan` keep that hover (and the click)
    // from starting a canvas gesture.
    'nodrag nopan pointer-events-auto',
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {builder ? (
          <button
            type="button"
            className={cn(
              className,
              'cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
            )}
            aria-label={gt('Open the expression for {socket}', {
              context:
                'canvas badge on an expression-fed socket; opens the builder',
              socket: nodeData.targetSocketName,
            })}
            onClick={(event) => {
              event.stopPropagation();
              builder.open(nodeData.targetNodeId, nodeData.targetSocketId);
            }}
          >
            {body}
          </button>
        ) : (
          <div className={className}>{body}</div>
        )}
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-96 space-y-1.5 px-2.5 py-2">
        <div className="font-mono text-[11px] font-semibold">
          {nodeData.name}
        </div>
        {nodeData.description && <p>{nodeData.description}</p>}
        {nodeData.arity && <p className="opacity-70">{nodeData.arity}</p>}

        {nodeData.args.length > 0 && (
          <div className="space-y-0.5 pt-0.5">
            <div className="opacity-70">
              <T context="heading above the argument list in a socket-expression tooltip">
                Arguments
              </T>
            </div>
            {nodeData.args.map((arg) => (
              <div key={arg.position} className="flex gap-1.5">
                <span className="opacity-70 tabular-nums">{arg.position}</span>
                <span className="font-mono">{arg.source}</span>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-current/20 pt-1.5 font-mono opacity-80">
          {nodeData.expression}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
